import type { SurfDeck, SurfScriptTone, SurfSlide } from "@vida/surf-core";
import {
  durationChipStatus,
  estimateSeconds,
  formatDurationSeconds,
  pdfEmbeddedFontName,
  resolveSpeakerScriptConfig,
  resolveTargetSeconds,
  totalEstimatedSeconds,
} from "@vida/surf-core";
import { ensureSurfPdfFonts } from "./surfPdfFonts.js";
import {
  drawSlideInRect,
  prepareSurfPdfDrawContext,
  slideBackgroundPayload,
  triggerSurfPdfDownload,
  type SurfPdfExportInput,
} from "./surfPdfExport.js";
import { drawSpeakerScriptRichText, drawSpeakerNoteCards } from "./speakerScriptPdfRichText.js";

/** Portrait A4 layout (mm) — tweak `LAYOUT_SCALE` to rescale thumbnails and chrome. */
const LAYOUT_SCALE = 1.5;

const PAGE = {
  W: 210,
  H: 297,
  MARGIN: 16,
} as const;

const CONTENT_W = PAGE.W - PAGE.MARGIN * 2;

const THUMB_BASE_W = 46;
const THUMB = {
  W: THUMB_BASE_W * LAYOUT_SCALE,
  H: ((THUMB_BASE_W * 9) / 16) * LAYOUT_SCALE,
} as const;

const PAGE_BG = [11, 17, 32] as const;
const LABEL_COLOR = [148, 163, 184] as const;
const TITLE_COLOR = [248, 250, 252] as const;
const SUBTITLE_COLOR = [100, 116, 139] as const;

const SURF_SANS = "Surf Inter";

const TONE_LABELS: Record<SurfScriptTone, string> = {
  executive: "Esecutivo",
  didactic: "Didattico",
  narrative: "Narrativo",
  casual: "Informale",
  persuasive: "Persuasivo",
};

const CHIP_COLORS: Record<
  ReturnType<typeof durationChipStatus>,
  { bg: [number, number, number]; text: [number, number, number] }
> = {
  ok: { bg: [20, 83, 45], text: [134, 239, 172] },
  warn: { bg: [120, 53, 15], text: [252, 211, 77] },
  over: { bg: [127, 29, 29], text: [252, 165, 165] },
};

const FOOTER_RESERVE_MM = 9;

function surfFont(weight: number): string {
  return pdfEmbeddedFontName(SURF_SANS, weight);
}

function slideLabel(slide: SurfSlide, index: number): string {
  const title = slide.layers
    .find((l) => l.type === "text" && l.textRole === "title")
    ?.content?.trim();
  return title ? `${index + 1}. ${title}` : `Slide ${index + 1}`;
}

function drawDarkPageBackground(pdf: import("jspdf").jsPDF): void {
  pdf.setFillColor(...PAGE_BG);
  pdf.rect(0, 0, PAGE.W, PAGE.H, "F");
}

function deckTotalSeconds(
  deck: SurfDeck,
  config: ReturnType<typeof resolveSpeakerScriptConfig>,
): number {
  const target = (config.totalTargetMinutes ?? 0) * 60;
  return target > 0 ? target : totalEstimatedSeconds(deck);
}

function cumulativeEstimatedSeconds(
  deck: SurfDeck,
  throughSlideIndex: number,
  wpm: number,
): number {
  let sum = 0;
  for (let j = 0; j <= throughSlideIndex; j += 1) {
    const slide = deck.slides[j]!;
    sum += estimateSeconds(slide.speakerScript?.text ?? "", wpm);
  }
  return sum;
}

function cumulativeTargetSeconds(deck: SurfDeck, throughSlideIndex: number): number {
  let sum = 0;
  for (let j = 0; j <= throughSlideIndex; j += 1) {
    sum += resolveTargetSeconds(deck, deck.slides[j]!.id);
  }
  return sum;
}

function cumulativeSecondsForSlide(
  deck: SurfDeck,
  slideIndex: number,
  config: ReturnType<typeof resolveSpeakerScriptConfig>,
): number {
  if ((config.totalTargetMinutes ?? 0) > 0) {
    return cumulativeTargetSeconds(deck, slideIndex);
  }
  return cumulativeEstimatedSeconds(deck, slideIndex, config.wordsPerMinute);
}

function formatFooterTiming(cumulativeSec: number, totalSec: number): string {
  return `${formatDurationSeconds(cumulativeSec)} su ${formatDurationSeconds(totalSec)} minuti`;
}

function contentMaxY(): number {
  return PAGE.H - PAGE.MARGIN - FOOTER_RESERVE_MM;
}

/** Footer: cumulative script time through this slide vs deck total. */
function drawPageFooter(
  pdf: import("jspdf").jsPDF,
  cumulativeSec: number,
  totalSec: number,
): void {
  if (totalSec <= 0) return;
  const label = formatFooterTiming(cumulativeSec, totalSec);
  pdf.setFont(surfFont(400), "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...LABEL_COLOR);
  const y = PAGE.H - PAGE.MARGIN + 1;
  pdf.text(label, PAGE.W / 2, y, { align: "center" });
}

function drawDurationChip(
  pdf: import("jspdf").jsPDF,
  x: number,
  y: number,
  estimated: number,
  target: number,
): number {
  const status = durationChipStatus(estimated, target);
  const colors = CHIP_COLORS[status];
  const label = `${formatDurationSeconds(estimated)} / ${formatDurationSeconds(target)}`;
  const fontSize = 8 * LAYOUT_SCALE;
  pdf.setFont(surfFont(400), "normal");
  pdf.setFontSize(fontSize);
  const padX = 2.5 * LAYOUT_SCALE;
  const padY = 1.2 * LAYOUT_SCALE;
  const textW = pdf.getTextWidth(label);
  const chipW = textW + padX * 2;
  const chipH = fontSize * 0.7;
  const chipX = x - chipW;
  pdf.setFillColor(...colors.bg);
  pdf.roundedRect(chipX, y - chipH + 1.5, chipW, chipH, 1, 1, "F");
  pdf.setTextColor(...colors.text);
  pdf.text(label, chipX + padX, y - padY);
  return chipX;
}

function sectionHeaderFontSize(continuation?: boolean): number {
  return (continuation ? 9 : 11) * LAYOUT_SCALE;
}

function drawSlideSectionHeader(
  pdf: import("jspdf").jsPDF,
  label: string,
  opts: {
    continuation?: boolean;
    estimated?: number;
    target?: number;
  },
): number {
  const fontSize = sectionHeaderFontSize(opts.continuation);
  const y = PAGE.MARGIN + 5;
  pdf.setFont(surfFont(700), "normal");
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...TITLE_COLOR);
  const display = opts.continuation ? `(segue) ${label}` : label;
  const maxLabelW =
    opts.estimated != null && opts.target != null ? CONTENT_W - 40 * LAYOUT_SCALE : CONTENT_W;
  const lines = pdf.splitTextToSize(display, maxLabelW) as string[];
  pdf.text(lines[0] ?? display, PAGE.MARGIN, y);
  if (opts.estimated != null && opts.target != null) {
    drawDurationChip(pdf, PAGE.W - PAGE.MARGIN, y, opts.estimated, opts.target);
  }
  return y + fontSize * 0.45 + 2 * LAYOUT_SCALE;
}

function drawCoverPage(
  pdf: import("jspdf").jsPDF,
  input: SurfPdfExportInput,
): void {
  const { deck } = input;
  const config = resolveSpeakerScriptConfig(deck);
  const totalEst = totalEstimatedSeconds(deck);
  const totalTarget = (config.totalTargetMinutes ?? 0) * 60;

  drawDarkPageBackground(pdf);

  let y = PAGE.H * 0.32;
  pdf.setFont(surfFont(700), "normal");
  pdf.setFontSize(22);
  pdf.setTextColor(...TITLE_COLOR);
  const titleLines = pdf.splitTextToSize(deck.title || "Presentazione", CONTENT_W) as string[];
  for (const line of titleLines) {
    pdf.text(line, PAGE.MARGIN, y);
    y += 10;
  }

  y += 6;
  pdf.setFont(surfFont(600), "normal");
  pdf.setFontSize(14);
  pdf.setTextColor(...SUBTITLE_COLOR);
  pdf.text("Discorsi speaker", PAGE.MARGIN, y);

  y += 14;
  pdf.setFont(surfFont(400), "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(...LABEL_COLOR);

  const meta: string[] = [
    `Tono: ${TONE_LABELS[config.defaultTone]}`,
    config.language ? `Lingua: ${config.language}` : null,
    totalTarget > 0
      ? `Durata target: ${formatDurationSeconds(totalTarget)} (stima ${formatDurationSeconds(totalEst)})`
      : `Durata stimata: ${formatDurationSeconds(totalEst)}`,
    `${deck.slides.length} slide`,
  ].filter((s): s is string => Boolean(s));

  for (const line of meta) {
    pdf.text(line, PAGE.MARGIN, y);
    y += 6.5;
  }
}

/**
 * Export portrait booklet: cover + one section per slide (small thumbnail, full-width script, multi-page flow).
 */
export async function exportSpeakerScriptBookletPdf(
  input: SurfPdfExportInput,
  filename?: string,
): Promise<Blob> {
  const { jsPDF } = await import("jspdf");
  const { deck, slideBackgroundUrls = {} } = input;
  if (!deck.slides.length) throw new Error("Deck senza slide.");

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  await ensureSurfPdfFonts(pdf);

  const config = resolveSpeakerScriptConfig(deck);
  const { layout, layerPayloads, imageCache, containAspects } = await prepareSurfPdfDrawContext(input);
  const totalSec = deckTotalSeconds(deck, config);
  const cumulativeBySlide = deck.slides.map((_, idx) =>
    cumulativeSecondsForSlide(deck, idx, config),
  );

  drawCoverPage(pdf, input);
  drawPageFooter(pdf, 0, totalSec);

  for (let i = 0; i < deck.slides.length; i += 1) {
    const slide = deck.slides[i]!;
    const label = slideLabel(slide, i);
    const script = slide.speakerScript?.text ?? "";
    const targetSeconds = resolveTargetSeconds(deck, slide.id);
    const estimated = estimateSeconds(script, config.wordsPerMinute);

    pdf.addPage("a4", "portrait");
    drawDarkPageBackground(pdf);
    drawPageFooter(pdf, cumulativeBySlide[i]!, totalSec);

    const headerBottom = drawSlideSectionHeader(pdf, label, { estimated, target: targetSeconds });

    const thumbGap = 3 * LAYOUT_SCALE;
    const scriptGap = 6 * LAYOUT_SCALE;
    const thumbX = PAGE.MARGIN;
    const thumbY = headerBottom + thumbGap;
    const bg = slideBackgroundPayload(slide.id, slideBackgroundUrls, imageCache);
    drawSlideInRect(
      pdf,
      { x: thumbX, y: thumbY, w: THUMB.W, h: THUMB.H },
      slide,
      deck.theme,
      bg,
      layerPayloads,
      layout,
      containAspects,
    );

    const onPageBreak = () => {
      pdf.addPage("a4", "portrait");
      drawDarkPageBackground(pdf);
      drawPageFooter(pdf, cumulativeBySlide[i]!, totalSec);
      const contBottom = drawSlideSectionHeader(pdf, label, { continuation: true });
      return {
        cy: contBottom + 2 * LAYOUT_SCALE,
        maxY: contentMaxY(),
      };
    };

    const scriptEndY = drawSpeakerScriptRichText(
      pdf,
      script,
      {
        x: PAGE.MARGIN,
        y: thumbY + THUMB.H + scriptGap,
        w: CONTENT_W,
        maxY: contentMaxY(),
      },
      onPageBreak,
    );

    const notes = slide.speakerScript?.notes ?? [];
    if (notes.length > 0) {
      drawSpeakerNoteCards(
        pdf,
        notes,
        {
          x: PAGE.MARGIN,
          y: scriptEndY + 4 * LAYOUT_SCALE,
          w: CONTENT_W,
          maxY: contentMaxY(),
        },
        onPageBreak,
      );
    }
  }

  const base = (filename ?? `${deck.title || "discorsi"}-speaker.pdf`).replace(/[^\w.\- ]+/g, "_");
  const safeName = base.endsWith(".pdf") ? base : `${base}.pdf`;
  const blob = pdf.output("blob");
  triggerSurfPdfDownload(blob, safeName);
  return blob;
}
